import {useRef, useEffect, useMemo, Suspense} from 'react'
import {useInfiniteThreadsPageData} from '../../../page-data/loaders/use-threads'
import {useDiffPathOrderMap, usePullRequestBasePath, useThreads} from '../../../stores/layout-selectors'
import {sortThreadPreviewsByDiffOrder} from '../../../helpers/comments-panel-helper'
import {useCompositeThreadPositioning} from '../../../hooks/use-composite-thread-positioning'
import {useDiffEntries} from '../../../stores/selectors'
import {CommentAction, getConversationPriority} from './comment/CommentAction'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {ClientOnly} from '../../ClientOnly'
import {PullRequestStoreProvider} from '../../../stores/PullRequestStore'
import {useBaseComparisonInfoQuery} from '../data/route-queries'
import {getCurrentUserLogin} from '@github-ui/client-env'

const SEVERITY_ORDER = {high: 0, med: 1, low: 2} as const

/**
 * Renders the comments actionability section. Wraps its subtree with ClientOnly +
 * Suspense + ErrorBoundary so that Alloy SSR never reaches the Deferred
 * base_comparison_info query (which would suspend renderToString).
 */
export function CommentsSection() {
  return (
    <ErrorBoundary boundaryName="CommentsSection" fallback={null}>
      <Suspense fallback={null}>
        <ClientOnly serverFallback={null}>
          <CommentsSectionWithStore />
        </ClientOnly>
      </Suspense>
    </ErrorBoundary>
  )
}

function CommentsSectionWithStore() {
  const {data: baseComparisonInfoData} = useBaseComparisonInfoQuery()
  return (
    <PullRequestStoreProvider initData={baseComparisonInfoData}>
      <CommentsSectionContent />
    </PullRequestStoreProvider>
  )
}

function CommentsSectionContent() {
  const threads = useThreads()
  const diffPathOrderMap = useDiffPathOrderMap()
  const viewerLogin = getCurrentUserLogin() ?? ''

  const threadPreviews = useMemo(() => {
    const sorted = sortThreadPreviewsByDiffOrder(threads, diffPathOrderMap)
    return [...sorted].sort(
      (a, b) =>
        SEVERITY_ORDER[getConversationPriority(a, viewerLogin)] -
        SEVERITY_ORDER[getConversationPriority(b, viewerLogin)],
    )
  }, [threads, diffPathOrderMap, viewerLogin])
  useCompositeThreadPositioning(threadPreviews, useDiffEntries())

  const pathName = usePullRequestBasePath()

  const {hasNextPage, isFetchingNextPage, fetchNextPage} = useInfiniteThreadsPageData({
    pathName,
    hasMorePages: true,
  })

  const fetchingRef = useRef(false)
  useEffect(() => {
    const fetchAllPages = async () => {
      if (hasNextPage && !isFetchingNextPage && !fetchingRef.current) {
        fetchingRef.current = true
        try {
          await fetchNextPage()
        } finally {
          fetchingRef.current = false
        }
      }
    }
    void fetchAllPages()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  return (
    <ul className="my-2 d-flex flex-column gap-2 list-style-none">
      {threadPreviews.map(thread => (
        <CommentAction key={thread.id} thread={thread} />
      ))}
    </ul>
  )
}
